<template>
  <loading-page
    v-if="gettingUpcomingInvoice || invoicesLoading"
    class="loading-invoice"
  />
  <div
    v-else
    class="payment mb-2"
  >

    <div class="default-table">
      <invoice-table
        class="top-fade-in"
        :invoice="upcomingInvoice"
        :currency-symbol="currencySymbol || ''"
        :getting-invoice="gettingUpcomingInvoice"
      >
        <a
          v-if="upcomingInvoice.lines"
          class="link d-flex align-items-center"
          @click="$router.push({ name: 'invoice-details', params: { id: 'upcoming', isUpcoming: true } })"
        >
          View full invoice for {{ currencySymbol }}{{ upcomingInvoice.total }}
          <GjIcon
            class="icon-right"
            name="ArrowRight"
          />
        </a>
      </invoice-table>
    </div>
    <div
      class="pb-1"
      :class="currentBreakPoint === 'lg' || currentBreakPoint === 'xl' ? 'default-table': 'mt-2'"
    >
      <div class="table-actions d-sm-flex justify-content-between">
        <h5 class="text-main">
          Invoices
        </h5>
      </div>
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          :items="invoices"
          :fields="fields"
          class="loaded"
          show-empty
          responsive
        >
          <template #cell(amount)="data">
            <div class="row">
              <div class="col-6">
                {{ getCurrency(data.item.currency) }}{{ data.value / 100 }}
              </div>
              <div class="col-6">
                <span
                  class="text-capitalize tag"
                  :class="data.item.status"
                >{{ data.item.status }}</span>
              </div>
            </div>
          </template>

          <template #cell(number)="data">
            <a
              v-b-tooltip.hover.v-primary="'View invoice details'"
              class="link"
              @click="$router.push({ name: 'invoice-details', params: { id: data.item.id } })"
            >{{ data.value }}</a>
            <a
              v-b-tooltip.hover.v-primary="'View Stripe Invoice'"
              class="icon-right"
              :href="data.item.hostedInvoiceUrl"
              target="_blank"
            >
              <img
                class="icon-left"
                width="20"
                :src="require('@/assets/svg/redirect.svg')"
              >
            </a>
          </template>

          <template #cell(created)="data">
            {{ formatOffsetDateTime(data.value) }}
          </template>

          <template #empty>
            <div
              class="d-flex justify-content-center align-items-center"
              style="height: 220px"
            >
              No records.
            </div>
          </template>
        </b-table>
        <table-busy v-if="fetchingInvoices" />
      </div>
      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-1">
        <template v-for="card in invoices">
          <card
            :key="card.id"
            :fields="fields"
            :item="card"
          >
            <template #amount>
              <div class="row w-100">
                <div class="col-sm-6">
                  {{ getCurrency(card.currency) }}{{ card.amount / 100 }}
                </div>
                <div class="col-sm-6 mt-1 mt-sm-0">
                  <span
                    class="text-capitalize tag"
                    :class="card.status"
                  >{{ card.status }}</span>
                </div>
              </div>
            </template>

            <template #number>
              <a
                v-b-tooltip.hover.v-primary="'View invoice details'"
                class="link"
                @click="$router.push({ name: 'invoice-details', params: { id: card.id } })"
              >{{ card.number }}</a>
              <a
                v-b-tooltip.hover.v-primary="'View Stripe Invoice'"
                class="icon-right"
                :href="card.hostedInvoiceUrl"
                target="_blank"
              >
                <img
                  class="icon-left"
                  width="20"
                  :src="require('@/assets/svg/redirect.svg')"
                >
              </a>
            </template>

            <template #created>
              {{ formatOffsetDateTime(card.created) }}
            </template>
          </card>
        </template>
        <template v-if="invoices.length === 0">
          <div
            v-if="fetchingInvoices"
            class="mobile-loading"
          >
            <table-busy />
          </div>
          <paper-simple v-else>
            No records.
          </paper-simple>
        </template>
      </div>
      <small-pagination
        v-if="resultInfo.hasMore || invoices.length > limit"
        class="mx-1"
        :show-less="invoices.length > limit"
        :show-more="resultInfo.hasMore"
        @less="showInvoices(true)"
        @more="showInvoices(false)"
      />
    </div>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'
import SmallPagination from '../parts/SmallPagination.vue'
import InvoiceTable from '../parts/InvoiceTable.vue'
import currencies from '../currencies'

export default {
  name: 'Invoices',
  components: {
    Card, SmallPagination, InvoiceTable,
  },
  data() {
    return {
      fields: [
        {
          key: 'amount', isPrimary: true,
        },
        { key: 'number', label: 'Invoice Number', isPrimary: true },
        { key: 'customer', variant: 'name' },
        { key: 'created', variant: 'time' },
      ],
      currencies,
      limit: 10,
      invoicesLoading: false,
    }
  },
  computed: {
    fetchingInvoices() {
      return this.$store.state.payments.fetchingInvoices
    },
    invoices() {
      return this.$store.state.payments.invoices
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    resultInfo() {
      return this.$store.state.payments.invoicesResultInfo || {}
    },
    upcomingInvoice() {
      return this.$store.state.payments.upcomingInvoice
    },
    gettingUpcomingInvoice() {
      return this.$store.state.payments.gettingUpcomingInvoice
    },
    currencySymbol() {
      const el = this.currencies.find(element => element.code === this.upcomingInvoice.currency)
      return el ? el.symbol : this.upcomingInvoice.currency
    },
  },
  created() {
    this.$store.dispatch('payments/fetchInvoices', { limit: this.limit }).then(() => { this.invoicesLoading = false })
    this.$store.dispatch('payments/getUpcomingInvoice')
  },
  methods: {
    getCurrency(curr) {
      const el = this.currencies.find(element => element.code === curr)
      return el ? el.symbol : curr
    },
    showInvoices(less) {
      if (less) {
        this.$store.commit('payments/RESET_INVOICES', this.limit)
        return
      }
      const query = { limit: this.limit, startingAfter: this.invoices[this.invoices.length - 1].id }
      this.$store.dispatch('payments/fetchInvoices', query)
    },
  },
}
</script>
<style lang="scss" scoped>
.tag {
  text-transform: capitalize;
  box-sizing: border-box;
  border-radius: 20px;
  padding: 2px 8px;
  color: var(--white);
  font-weight: bold;
  &.paid {
    background: var(--success);
    border: 1px solid var(--success);
  }
  &.draft {
    background: #667c99;
    border: 1px solid #667c99;
  }
  &.open {
    background: var(--primary);
    border: 1px solid var(--primary);
  }
  &.void {
    background: var(--warning);
    border: 1px solid var(--warning);
  }
  &.uncollectible {
    background: var(--danger);
    border: 1px solid var(--danger);
  }
}
.loading-invoice {
  min-height: 478px;
  margin: 0 !important;
}
</style>
